/**
 * The URL is the list's state (HS-1443).
 *
 * The filter builder's rows (`?filters=`, written by `useTableFilterConditions`) and the search box
 * (`?q=`) live in the query string, so a reload or a shared link reproduces the view. There is no
 * page parameter: the feed loads by scrolling, and a position deep in an infinite list is not
 * something a link can honestly promise.
 *
 * This module owns the two translations that go with that: the route's schema, and the mapping from
 * the builder's rows onto the endpoint's `status` / `kind` / `q`.
 */
import { filterConditionSchema } from '@smartsash/data-screen';
import { z } from 'zod';

import { isKnownKind } from './notifications.adapter';
import type { NotificationFilters } from './notifications.api';
import {
  NOTIFICATION_STATUSES,
  type NotificationKind,
  type NotificationStatus,
} from './notifications.types';

/** The two fields the builder offers: the design's Status, and the ticket's kind filter. */
export const NOTIFICATION_FILTER_FIELDS = {
  status: 'status',
  kind: 'kind',
} as const;

/** Longer than any real search; a pasted essay is cut rather than sent. */
export const SEARCH_MAX_LENGTH = 200;

/**
 * A plain object rather than `createListSearchSchema`: that shape carries `page` and `pageSize` as
 * required outputs, which would force every link to `/notifications` to invent a page for a list
 * that has none. Both fields here are optional, so a bare `<Link to="/notifications" />` is valid.
 */
export const notificationsSearchSchema = z.object({
  q: z.string().trim().optional().catch(undefined),
  /**
   * Rows are validated one at a time, as on My Sessions: one hand-edited bad row must not throw
   * away the good one beside it.
   */
  filters: z
    .array(z.unknown())
    .optional()
    .catch(undefined)
    .transform((rows) =>
      rows
        ?.map((row) => filterConditionSchema.safeParse(row))
        .filter((result) => result.success)
        .map((result) => result.data),
    ),
});

export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;

function isStatus(value: string | undefined): value is NotificationStatus {
  return (NOTIFICATION_STATUSES as readonly (string | undefined)[]).includes(value);
}

/** The URL state as the filters the query takes. Anything unrecognised narrows nothing. */
export function toNotificationFilters(search: NotificationsSearch): NotificationFilters {
  let status: NotificationStatus = 'all';
  const kinds = new Set<NotificationKind>();

  for (const condition of search.filters ?? []) {
    if (condition.field === NOTIFICATION_FILTER_FIELDS.status) {
      const value = condition.values[0];
      if (isStatus(value)) status = value;
    } else if (condition.field === NOTIFICATION_FILTER_FIELDS.kind) {
      for (const value of condition.values) if (isKnownKind(value)) kinds.add(value);
    }
  }

  const q = (search.q ?? '').trim().slice(0, SEARCH_MAX_LENGTH);

  return {
    status,
    // Sorted so the same selection in a different order is the same cache entry.
    ...(kinds.size > 0 ? { kind: [...kinds].sort() } : {}),
    ...(q === '' ? {} : { q }),
  };
}

/**
 * Which empty body an empty answer gets (contract §6):
 *
 * - `none` — nothing narrows the view: the user has no notifications at all (Figma 04).
 * - `caughtUp` — the only narrowing is Status = Unread: everything has been read.
 * - `noMatch` — any other filter or a search: offer to clear them.
 */
export type NotificationsEmptyVariant = 'none' | 'caughtUp' | 'noMatch';

export function emptyVariantFor(filters: NotificationFilters): NotificationsEmptyVariant {
  const narrowedOtherwise = (filters.kind?.length ?? 0) > 0 || (filters.q ?? '') !== '';
  if (!narrowedOtherwise && (filters.status ?? 'all') === 'all') return 'none';
  if (!narrowedOtherwise && filters.status === 'unread') return 'caughtUp';
  return 'noMatch';
}
